import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, FileSpreadsheet, Loader2, ChevronLeft, CheckCircle2,
  AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";

// Canonical CRM fields the user can map their spreadsheet columns to.
const TARGET_FIELDS = [
  { key: "first_name", label: "Vorname" },
  { key: "last_name", label: "Nachname" },
  { key: "company_name", label: "Firmenname" },
  { key: "email", label: "E-Mail" },
  { key: "phone", label: "Telefon / Handy" },
  { key: "address", label: "Strasse + Nr." },
  { key: "postal_code", label: "PLZ" },
  { key: "city", label: "Ort" },
  { key: "canton", label: "Kanton" },
  { key: "country", label: "Land" },
  { key: "date_of_birth", label: "Geburtsdatum" },
  { key: "place_of_birth", label: "Geburtsort" },
  { key: "nationality", label: "Nationalität" },
  { key: "permit_type", label: "Ausweis" },
  { key: "preferred_language", label: "Sprache" },
  { key: "civil_status", label: "Zivilstand" },
  { key: "housing_situation", label: "Wohnsituation" },
  { key: "ahv_number", label: "AHV-Nr." },
  { key: "employer", label: "Arbeitgeber" },
  { key: "notes", label: "Notizen" },
];

const NONE = "__none__";

export default function ImportContactFromExcelDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("upload"); // upload | map | done
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mapping, setMapping] = useState({}); // {field_key: column_header}
  const [skipDup, setSkipDup] = useState(true);
  const [defaultType, setDefaultType] = useState("person");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setStep("upload");
    setBusy(false);
    setFilename("");
    setColumns([]);
    setRows([]);
    setTotalRows(0);
    setMapping({});
    setSkipDup(true);
    setDefaultType("person");
    setResult(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const okExt = /\.(xlsx|xls|csv|tsv|txt)$/i.test(file.name || "");
    if (!okExt) {
      toast.error("Format nicht unterstützt — bitte .xlsx oder .csv");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/clients/import-file/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { columns: cols, suggested_mapping, preview_rows, total_rows, filename: fn } = res.data;
      if (!cols || cols.length === 0) {
        toast.error("Keine Spalten erkannt");
        return;
      }
      setColumns(cols);
      setRows(preview_rows || []);
      setTotalRows(total_rows || 0);
      setMapping(suggested_mapping || {});
      setFilename(fn || file.name);
      setStep("map");
      toast.success(`${total_rows} Zeilen erkannt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadFile(f);
  };

  const setMap = (field, col) => {
    setMapping((m) => {
      const next = { ...m };
      if (col === NONE) delete next[field];
      else next[field] = col;
      return next;
    });
  };

  const commit = async () => {
    if (!mapping.first_name && !mapping.last_name && !mapping.company_name) {
      toast.error("Bitte mindestens Vorname/Nachname oder Firma zuordnen");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/clients/import-file/commit", {
        rows,
        mapping,
        skip_duplicates: skipDup,
        default_client_type: defaultType,
      });
      setResult(res.data);
      setStep("done");
      if (res.data.created_count > 0) {
        toast.success(`${res.data.created_count} Kunden importiert`);
      } else if (res.data.skipped_count > 0) {
        toast.info(`Alle ${res.data.skipped_count} Zeilen waren Duplikate`);
      } else {
        toast.warning("Keine Kunden importiert");
      }
      onImported?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // helper: which columns are still unassigned?
  const usedColumns = new Set(Object.values(mapping));
  const previewLimit = 5;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="import-excel-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-prime" />
            {step === "upload" && "Aus Excel / CSV importieren"}
            {step === "map" && "Spalten zuordnen"}
            {step === "done" && "Import abgeschlossen"}
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1 — upload */}
        {step === "upload" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Excel- (.xlsx) oder CSV-Datei mit Ihren Kontakten hochladen.
              Im nächsten Schritt können Sie die Spalten den CRM-Feldern zuordnen.
            </p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                dragOver ? "border-prime bg-prime/5" : "border-border hover:border-prime/40 hover:bg-secondary/40"
              }`}
              data-testid="excel-dropzone"
            >
              <Upload className="w-10 h-10 mx-auto text-prime mb-3" />
              <div className="font-medium text-sm">Datei hier ablegen oder klicken</div>
              <div className="text-xs text-muted-foreground mt-1">
                Unterstützt: .xlsx, .xls, .csv · max 10 MB
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv,.tsv,.txt"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                data-testid="excel-file-input"
              />
            </div>
            {busy && (
              <div className="flex items-center gap-2 text-sm text-prime">
                <Loader2 className="w-4 h-4 animate-spin" /> Datei wird gelesen…
              </div>
            )}
            <div className="bg-secondary/40 border border-border rounded-md p-3 text-xs text-muted-foreground">
              <div className="font-semibold text-foreground mb-1">Tipp — Spaltennamen</div>
              Verwenden Sie aussagekräftige Spalten-Überschriften (z. B. {`«Vorname»`},
              {` «E-Mail», «PLZ»`}). Die KI erkennt deutsch, italienisch, französisch
              und englisch und schlägt automatisch passende Felder vor.
            </div>
          </div>
        )}

        {/* STEP 2 — map columns */}
        {step === "map" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="font-mono font-medium">{filename}</span>{" "}
                <span className="text-muted-foreground">· {totalRows} Zeilen erkannt</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Standard-Typ:</Label>
                  <Select value={defaultType} onValueChange={setDefaultType}>
                    <SelectTrigger className="h-8 w-32" data-testid="default-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="person">Person</SelectItem>
                      <SelectItem value="business">Unternehmen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="skip-dup"
                    checked={skipDup}
                    onCheckedChange={(v) => setSkipDup(!!v)}
                    data-testid="skip-dup-checkbox"
                  />
                  <Label htmlFor="skip-dup" className="text-xs cursor-pointer">
                    Duplikate überspringen (E-Mail / Telefon)
                  </Label>
                </div>
              </div>
            </div>

            {/* Mapping grid */}
            <div className="prime-card p-4">
              <div className="text-xs font-semibold label-uppercase mb-3">Feldzuordnung</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {TARGET_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="label-uppercase text-[10px] mb-1 block">
                      {f.label}
                      {["first_name", "last_name", "company_name"].includes(f.key) && (
                        <span className="text-prime ml-1">·</span>
                      )}
                    </Label>
                    <Select
                      value={mapping[f.key] || NONE}
                      onValueChange={(v) => setMap(f.key, v)}
                    >
                      <SelectTrigger className="h-9 text-xs" data-testid={`map-${f.key}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value={NONE}>— (nicht zuordnen)</SelectItem>
                        {columns.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                            {usedColumns.has(c) && mapping[f.key] !== c && (
                              <span className="text-amber-600 ml-1">·</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-3">
                · Mindestens eines von Vorname / Nachname / Firma erforderlich
              </div>
            </div>

            {/* Preview rows */}
            <div className="prime-card p-4">
              <div className="text-xs font-semibold label-uppercase mb-3">
                Vorschau (erste {Math.min(previewLimit, rows.length)} von {totalRows})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      {columns.map((c) => (
                        <th key={c} className="text-left p-2 font-semibold whitespace-nowrap">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, previewLimit).map((r, i) => (
                      <tr key={i} className="border-b border-border/40">
                        {columns.map((c) => (
                          <td key={c} className="p-2 whitespace-nowrap text-muted-foreground">
                            {String(r[c] ?? "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — result */}
        {step === "done" && result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="prime-card p-4 text-center">
                <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-600" />
                <div className="font-display text-3xl font-bold mt-2" data-testid="created-count">
                  {result.created_count}
                </div>
                <div className="label-uppercase text-[10px] text-muted-foreground mt-1">Importiert</div>
              </div>
              <div className="prime-card p-4 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto text-amber-500" />
                <div className="font-display text-3xl font-bold mt-2" data-testid="skipped-count">
                  {result.skipped_count}
                </div>
                <div className="label-uppercase text-[10px] text-muted-foreground mt-1">Duplikate</div>
              </div>
              <div className="prime-card p-4 text-center">
                <X className="w-6 h-6 mx-auto text-destructive" />
                <div className="font-display text-3xl font-bold mt-2" data-testid="error-count">
                  {result.error_count}
                </div>
                <div className="label-uppercase text-[10px] text-muted-foreground mt-1">Fehler</div>
              </div>
            </div>

            {result.skipped?.length > 0 && (
              <div className="prime-card p-4">
                <div className="text-xs font-semibold label-uppercase mb-2">Übersprungen</div>
                <ul className="text-xs font-mono space-y-1 max-h-40 overflow-y-auto">
                  {result.skipped.map((s, i) => (
                    <li key={i} className="text-muted-foreground">
                      Zeile {s.row} — {s.reason}: {s.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="prime-card p-4 border-destructive/40">
                <div className="text-xs font-semibold label-uppercase mb-2 text-destructive">Fehler</div>
                <ul className="text-xs font-mono space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-destructive">
                      Zeile {e.row} — {e.msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2">
          {step === "map" ? (
            <Button variant="outline" onClick={() => setStep("upload")} disabled={busy} data-testid="back-to-upload-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Andere Datei
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={busy} data-testid="excel-cancel-btn">
              {step === "done" ? "Schliessen" : "Abbrechen"}
            </Button>
            {step === "map" && (
              <Button onClick={commit} disabled={busy} className="btn-prime" data-testid="commit-import-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                {totalRows} Zeilen importieren
              </Button>
            )}
            {step === "done" && (
              <Button onClick={reset} className="btn-prime" data-testid="import-another-btn">
                <Upload className="w-4 h-4 mr-1" /> Weitere Datei
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
